import React, { useState, useEffect } from "react";
import {
  Plane,
  Calendar,
  MapPin,
  AlertCircle,
  ExternalLink,
  Shield,
  Phone,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Navigation,
  Baby,
  Car,
  Sun,
  Moon,
  Share2,
  Printer,
  Compass,
  Copy,
  Check,
  X,
} from "lucide-react";

const itineraryData = [
  {
    day: 1,
    title: "Arrival & Dubai Fountain Show",
    badge: "Must Visit",
    badgeColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    morning:
      "Land at Dubai International Airport (DXB) via Emirates Airlines. Transfer to hotel on Sheikh Zayed Road.",
    evening:
      "Visit The Dubai Fountain located right outside Dubai Mall (Waterfront Promenade). Shows run every 30 mins between 6:00 PM – 11:00 PM (5-7 mins each).",
    verdict:
      "Free access, no tickets required. Perfect easy start for families after a flight.",
    bookingUrl: null,
  },
  {
    day: 2,
    title: "Dubai Frame & Marina Dinner Cruise",
    badge: "Evening Cruise",
    badgeColor: "bg-sky-50 text-sky-700 border-sky-200",
    morning:
      "Dubai Frame (10:00 AM) — Great views of Old & New Dubai. Keep as an optional low priority stop if time permits.",
    evening:
      "Dubai Marina Dinner Cruise (8:30 PM – 10:00 PM) — Boarding at Dubai Marina. Enjoy a buffet dinner with live Arabic Tanoura dance show.",
    verdict: "Advance booking required for cruise.",
    bookingUrl:
      "https://www.klook.com/en-IN/destination/c78-dubai/1010-cruises/",
    frameUrl:
      "https://www.klook.com/en-IN/activity/30552-dubai-frame-tickets-round-trip-hotel-transfers/",
  },
  {
    day: 3,
    title: "Dubai Aquarium & Burj Khalifa",
    badge: "Highly Recommended",
    badgeColor: "bg-purple-50 text-purple-700 border-purple-200",
    morning:
      "Dubai Aquarium & Underwater Zoo (10:00 AM) inside Dubai Mall. Features walkthrough ocean tunnel & marine life.",
    evening:
      "At The Top, Burj Khalifa (6:00 PM) — Entry via Dubai Mall Lower Ground level. Visit 124th & 125th floors.",
    verdict:
      "⭐ Book Fast Track tickets to skip long elevator queues, especially with a 5-year-old child.",
    bookingUrl:
      "https://www.klook.com/en-IN/activity/2228-burj-khalifa-observation-deck-dubai/",
    aquariumUrl:
      "https://www.klook.com/en-IN/activity/9648-dubai-aquarium-underwater-zoo-dubai/",
  },
  {
    day: 4,
    title: "Warner Bros. World (Yas Island, Abu Dhabi)",
    badge: "Full Day Trip",
    badgeColor: "bg-amber-50 text-amber-700 border-amber-200",
    morning:
      "Board the FREE Shuttle Bus from Dubai to Yas Island, Abu Dhabi. Arrive at Warner Bros. World.",
    evening:
      "Enjoy immersive theme park rides & character meet-and-greets. Return to Dubai via the free return shuttle bus.",
    verdict:
      "⭐ Amazing for kids! Shuttle bus operates on a first-come, first-served basis—reach the stop early.",
    bookingUrl:
      "https://www.klook.com/en-IN/activity/11522-warner-bros-world-abu-dhabi-ticket-dubai/",
  },
  {
    day: 5,
    title: "The Green Planet & Family Desert Safari",
    badge: "Must Visit Safari",
    badgeColor: "bg-orange-50 text-orange-700 border-orange-200",
    morning:
      "The Green Planet (10:00 AM) — Indoor bio-dome. (Can skip as it's not high value).",
    evening:
      "Family Desert Safari (3:00 PM Pickup) — Dune rides, camel riding, falcon photos & buffet dinner show.",
    verdict:
      "⭐ Must Visit! Operator sends WhatsApp confirmation around 8:00 AM. Book VIP Pass for table service & better seating.",
    bookingUrl:
      "https://www.klook.com/en-IN/activity/144879-premium-desert-safari-special-shows-gourmet-food-at-noble-camp/",
    greenPlanetUrl:
      "https://www.klook.com/en-IN/activity/5798-green-planet-ticket-dubai/",
  },
  {
    day: 6,
    title: "Ski Dubai & Kite Beach",
    badge: "Snow & Beach",
    badgeColor: "bg-cyan-50 text-cyan-700 border-cyan-200",
    morning:
      "Ski Dubai Snow Park (10:00 AM) inside Mall of the Emirates Ground Floor. Enjoy snow tubing & sledding.",
    evening:
      "Kite Beach — Clean, open beach with views of Burj Al Arab. Ideal for kids to play in the sand.",
    verdict:
      "⚠ Very cold inside Ski Dubai! While gear is provided, bring your own monkey cap/beanie & warm gloves for your child.",
    bookingUrl:
      "https://www.klook.com/en-IN/activity/2243-ski-dubai-polar-pass-dubai/",
  },
  {
    day: 7,
    title: "Old Dubai, Shopping & Departure",
    badge: "Shopping & Flight",
    badgeColor: "bg-rose-50 text-rose-700 border-rose-200",
    morning:
      "Old Dubai (Deira / Bur Dubai) — Explore Gold Souk, Spice Souk, camel chocolates, dates & cheap Indian food.",
    evening:
      "Return to hotel, check out, transfer to DXB Airport for evening flight home.",
    verdict:
      "Note: Global Village & Legoland Water Park were closed for seasonal maintenance during off-peak dates.",
    bookingUrl: null,
  },
];

const travelTips = [
  {
    title: "Zero Forex Card",
    desc: "Use a Scapia Visa Card (or any zero-forex card) to avoid 3.5% forex fees.",
    icon: CreditCard,
  },
  {
    title: "Cash Needs",
    desc: "Carry 500 – 1,000 AED cash for small vendors/tips. Cards work in >95% places.",
    icon: Sparkles,
  },
  {
    title: "Taxi & Food Apps",
    desc: "Download Careem or Uber for hassle-free rides and hotel food delivery.",
    icon: Car,
  },
  {
    title: "UAE Visa",
    desc: "Apply via MakeMyTrip App. Takes ~2 days; standard digital photo uploads required.",
    icon: Shield,
  },
  {
    title: "International Roaming",
    desc: "Recharge Airtel IR pack before flying for bank OTPs (Airport gives free tourist SIM).",
    icon: Phone,
  },
  {
    title: "Metro & Transport",
    desc: "Use Red Line Metro for Dubai Mall sights. Purchase tickets at metro stations.",
    icon: Navigation,
  },
  {
    title: "Hotel Location",
    desc: "Stay on Sheikh Zayed Road for direct central access to major sights.",
    icon: MapPin,
  },
  {
    title: "Bring a Stroller",
    desc: "Essential for 5-year-old toddlers due to extensive walking inside Dubai Mall.",
    icon: Baby,
  },
];

export default function DubaiItinerary() {
  const [activeTab, setActiveTab] = useState("itinerary");

  // Helper to get state where all days are expanded
  const getAllExpandedState = () => {
    const all = {};
    itineraryData.forEach((d) => (all[d.day] = true));
    return all;
  };

  // 1. EXPANDED BY DEFAULT FOR ALL DAYS
  const [expandedDays, setExpandedDays] = useState(getAllExpandedState());

  // Share Modal & Toast States
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // 2. AUTOMATICALLY EXPAND ALL BEFORE PRINTING
  useEffect(() => {
    const handleBeforePrint = () => {
      setExpandedDays(getAllExpandedState());
    };

    window.addEventListener("beforeprint", handleBeforePrint);
    return () => window.removeEventListener("beforeprint", handleBeforePrint);
  }, []);

  const toggleDay = (day) => {
    setExpandedDays((prev) => ({ ...prev, [day]: !prev[day] }));
  };

  const expandAll = () => setExpandedDays(getAllExpandedState());
  const collapseAll = () => setExpandedDays({});

  // Trigger Print
  const handlePrint = () => {
    expandAll();
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // 3. AUTOMATICALLY EXPAND ALL ON SHARE
  const handleShare = async () => {
    expandAll(); // Ensure everything is expanded when sharing

    const shareData = {
      title: "Dubai Family Travel Itinerary",
      text: "Check out this 7-Day Kid-Friendly Dubai Itinerary!",
      url: window.location.href,
    };

    if (
      navigator.share &&
      navigator.canShare &&
      navigator.canShare(shareData)
    ) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (error.name !== "AbortError") {
          setIsShareModalOpen(true);
        }
      }
    } else {
      setIsShareModalOpen(true);
    }
  };

  const copyToClipboard = async () => {
    expandAll();
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100/70 text-slate-800 font-sans pb-16 antialiased">
      {/* Dynamic Print CSS Rules */}
      <style>{`
        @media print {
          /* Remove browser headers, footers, and margins */
          @page {
            margin: 12mm;
            size: auto;
          }
          body {
            background: #fff !important;
            color: #000 !important;
          }
          /* Hide non-printable controls */
          .no-print,
          button,
          .print-hidden {
            display: none !important;
          }
          /* Prevent cards from splitting across page breaks */
          .print-break-inside-avoid {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          /* Remove absolute positioning offsets on print */
          .print-static {
            position: static !important;
            margin-top: 0 !important;
          }
        }
      `}</style>

      {/* Top Accent Banner */}
      <div className="h-2 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 no-print" />

      {/* Hero Header Section */}
      <div className="relative overflow-hidden bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8 shadow-xl print:py-4 print:bg-white print:text-black">
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4 no-print">
            <div className="inline-flex items-center gap-2 bg-slate-800/80 border border-slate-700/60 backdrop-blur-md px-3.5 py-1.5 rounded-full text-xs font-medium text-amber-400">
              <Plane className="w-3.5 h-3.5 text-amber-400" />
              Emirates Airlines • 7 Days / 6 Nights
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700/60 rounded-lg transition"
              >
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold bg-orange-600 hover:bg-orange-500 text-white rounded-lg shadow-sm transition active:scale-95"
              >
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2 text-white print:text-slate-900">
            Dubai Family Travel Itinerary
          </h1>
          <p className="text-sm sm:text-base text-slate-300 print:text-slate-600 max-w-2xl font-light leading-relaxed">
            Curated 100% Kid-Centric Itinerary • Tailored for 5-Year-Old
            Toddlers • Sheikh Zayed Road Hub Base
          </p>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 print-static relative z-20">
        {/* Navigation Tabs */}
        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-lg p-1.5 flex gap-2 border border-slate-200/80 mb-8 no-print">
          <button
            onClick={() => setActiveTab("itinerary")}
            className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
              activeTab === "itinerary"
                ? "bg-slate-900 text-white shadow-md"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/70"
            }`}
          >
            <Calendar className="w-4 h-4 text-orange-400" /> Day-Wise Itinerary
          </button>
          <button
            onClick={() => setActiveTab("tips")}
            className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
              activeTab === "tips"
                ? "bg-slate-900 text-white shadow-md"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/70"
            }`}
          >
            <Compass className="w-4 h-4 text-orange-400" /> Essential Logistics
            & Tips
          </button>
        </div>

        {/* Tab 1: Itinerary */}
        {(activeTab === "itinerary" || window.matchMedia("print").matches) && (
          <div className="space-y-6">
            <div className="flex items-center justify-between px-2 text-xs text-slate-500 font-medium no-print">
              <span>Showing 7 Days Plan</span>
              <div className="flex gap-3">
                <button
                  onClick={expandAll}
                  className="hover:text-slate-900 underline underline-offset-2"
                >
                  Expand All
                </button>
                <span>•</span>
                <button
                  onClick={collapseAll}
                  className="hover:text-slate-900 underline underline-offset-2"
                >
                  Collapse All
                </button>
              </div>
            </div>

            {/* Timeline Day Cards */}
            <div className="relative border-l-2 border-slate-200/80 ml-4 sm:ml-6 space-y-6 pl-4 sm:pl-8 print:ml-0 print:border-none print:pl-0">
              {itineraryData.map((day) => {
                const isExpanded = expandedDays[day.day];
                return (
                  <div
                    key={day.day}
                    className="relative bg-white rounded-2xl shadow-xs border border-slate-200/90 overflow-hidden print-break-inside-avoid print:border-slate-300 print:mb-4"
                  >
                    <div className="absolute -left-[2.1rem] sm:-left-[3.1rem] top-5 w-8 h-8 rounded-full bg-slate-900 border-4 border-slate-100 text-amber-400 font-black text-xs flex items-center justify-center shadow-sm no-print">
                      D{day.day}
                    </div>

                    <div
                      onClick={() => toggleDay(day.day)}
                      className="p-5 flex items-center justify-between gap-4 cursor-pointer select-none bg-slate-50/50 hover:bg-slate-50 transition print:p-3 print:bg-slate-100"
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                          Day {day.day}: {day.title}
                        </h3>
                        <span
                          className={`px-3 py-0.5 rounded-full text-xs font-bold border ${day.badgeColor}`}
                        >
                          {day.badge}
                        </span>
                      </div>
                      <button className="text-slate-400 hover:text-slate-600 p-1 rounded-lg no-print">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="p-5 pt-2 border-t border-slate-100 space-y-4 print:p-3">
                        <div className="grid md:grid-cols-2 gap-4 text-sm mt-3 print:grid-cols-2 print:gap-2">
                          <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-100/80 print:p-2">
                            <div className="flex items-center gap-2 text-amber-800 font-bold mb-2 print:mb-1">
                              <Sun className="w-4 h-4 text-amber-600 no-print" />{" "}
                              Morning / Daytime
                            </div>
                            <p className="text-slate-700 leading-relaxed font-normal print:text-xs">
                              {day.morning}
                            </p>
                          </div>

                          <div className="bg-indigo-50/40 p-4 rounded-xl border border-indigo-100/80 print:p-2">
                            <div className="flex items-center gap-2 text-indigo-900 font-bold mb-2 print:mb-1">
                              <Moon className="w-4 h-4 text-indigo-600 no-print" />{" "}
                              Evening Activities
                            </div>
                            <p className="text-slate-700 leading-relaxed font-normal print:text-xs">
                              {day.evening}
                            </p>
                          </div>
                        </div>

                        <div className="bg-slate-900 text-slate-200 rounded-xl p-4 text-xs sm:text-sm flex items-start gap-3 shadow-inner print:bg-slate-100 print:text-slate-800 print:p-2">
                          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5 no-print" />
                          <div>
                            <span className="font-bold text-amber-400 print:text-slate-900 uppercase tracking-wider text-[11px] block mb-0.5">
                              Pro Tip / Verdict
                            </span>
                            <p className="leading-relaxed print:text-xs">
                              {day.verdict}
                            </p>
                          </div>
                        </div>

                        {(day.bookingUrl ||
                          day.aquariumUrl ||
                          day.frameUrl) && (
                          <div className="flex flex-wrap gap-2.5 pt-2 border-t border-slate-100 no-print">
                            {day.bookingUrl && (
                              <a
                                href={day.bookingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200/80 hover:bg-orange-100 px-3.5 py-2 rounded-xl transition shadow-2xs"
                              >
                                Reserve on Klook{" "}
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                            {day.aquariumUrl && (
                              <a
                                href={day.aquariumUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200/80 hover:bg-blue-100 px-3.5 py-2 rounded-xl transition shadow-2xs"
                              >
                                Aquarium Pass{" "}
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                            {day.frameUrl && (
                              <a
                                href={day.frameUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition shadow-2xs"
                              >
                                Dubai Frame Ticket{" "}
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab 2: Tips & Logistics */}
        {(activeTab === "tips" || window.matchMedia("print").matches) && (
          <div className="grid sm:grid-cols-2 gap-4 print:grid-cols-2 print:mt-6 print-break-inside-avoid">
            {travelTips.map((tip, idx) => {
              const Icon = tip.icon;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-5 shadow-xs border border-slate-200/90 hover:shadow-md transition flex items-start gap-4 print:p-3 print:border-slate-300"
                >
                  <div className="p-3 rounded-xl bg-orange-50 text-orange-600 border border-orange-100 shrink-0 no-print">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1 text-base print:text-sm">
                      {tip.title}
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed print:text-xs">
                      {tip.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Share Modal Dialog */}
      {isShareModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in no-print">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 relative">
            <button
              onClick={() => setIsShareModalOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-orange-50 text-orange-600 border border-orange-100">
                <Share2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  Share Itinerary
                </h3>
                <p className="text-xs text-slate-500">
                  Copy link to share with family or friends
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-2 mb-4">
              <input
                type="text"
                readOnly
                value={window.location.href}
                className="bg-transparent text-xs text-slate-700 flex-1 outline-none px-2 truncate"
              />
              <button
                onClick={copyToClipboard}
                className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${
                  copied
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-900 hover:bg-slate-800 text-white"
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" /> Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" /> Copy Link
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
